import { Injectable } from '@angular/core';
import {
  FrontendApiUpdateRecoveryFlowRequest,
  RecoveryFlow,
} from '@ory/client';
import { Observable } from 'rxjs';
import { OryCreateInput, OryExecuteInput, OryExecuteOutput, OryGetOutput } from '../../model';
import type { OryGetInput } from '../../model';
import { OryKratosBaseFlow } from './base.flow';
import { OryFlowReader } from '../../utils';
import type {
  OryCreatableFlow,
  OryRetrievableFlow,
  OryExecutableFlow,
} from '../flow-capabilities';

/**
 * Implements Ory Kratos recovery flow for account recovery via email or code.
 *
 * Supports recovery methods:
 * - Email recovery link
 * - Recovery code verification
 *
 * @remarks
 * Session loading after recovery is handled by canLoadSessionForSettings guard
 * for proper AAL2 redirect handling with return_to preservation.
 */
@Injectable({
  providedIn: 'root',
})
export class OryRecoveryFlow<IdentityTraits extends object = object>
  extends OryKratosBaseFlow
  implements
    OryCreatableFlow<'recovery', IdentityTraits>,
    OryRetrievableFlow<'recovery', IdentityTraits>,
    OryExecutableFlow<'recovery', IdentityTraits>
{

  create(
    input: OryCreateInput<'recovery'> = {},
  ): Observable<RecoveryFlow> {
    return this.fromOryRequest(
      this.iamApi.createBrowserRecoveryFlow(input),
    );
  }

  get(input: OryGetInput<'recovery'>): Observable<RecoveryFlow> {
    return this.fromOryRequest(this.iamApi.getRecoveryFlow(input));
  }

  execute(
    flow: OryGetOutput<'recovery', IdentityTraits>,
    input: OryExecuteInput<'recovery'>,
  ): Observable<OryExecuteOutput<'recovery', IdentityTraits>> {
    const csrfToken = OryFlowReader.csrfToken(flow);
    const apiRequest: FrontendApiUpdateRecoveryFlowRequest = {
      flow: flow.id,
      updateRecoveryFlowBody: {
        ...input,
        csrf_token: csrfToken,
      },
    };
    return this.fromOryRequest(this.iamApi.updateRecoveryFlow(apiRequest));
  }

}
